import { useCallback, useEffect, useMemo, useState } from "react";
import type { RentChange } from "../lib/realestate";
import { useUpdateRentChange } from "./realestate";
import {
  type DraftRentChangeRow,
  type ValidationResult,
  initDraftRowsMap,
  isRowChanged,
  validateDraftRow,
} from "./useRentChangeBulkEdit.helpers";

interface UseRentChangeBulkEditOptions {
  propertyId: string;
  tenantId: string;
  rentChanges: RentChange[];
  moveInDate: string;
  moveOutDate: string | null;
}

type SetRowPatch = Partial<
  Pick<DraftRentChangeRow, "effectiveFrom" | "coldRentCents" | "sideCostsAdvanceCents" | "notes">
>;

interface SaveState {
  loading: boolean;
  rowErrors: Map<string, ValidationResult | string>;
  lastSaveAttemptCount: number;
}

export interface UseRentChangeBulkEditResult {
  editMode: boolean;
  enterEditMode: () => void;
  cancelEdit: () => void;
  confirmDiscard: () => void;
  abortDiscardConfirmation: () => void;
  needsDiscardConfirmation: boolean;
  rows: Map<string, DraftRentChangeRow>;
  setRow: (id: string, patch: SetRowPatch) => void;
  changesCount: number;
  save: () => Promise<void>;
  saveState: SaveState;
}

export function useRentChangeBulkEdit(
  options: UseRentChangeBulkEditOptions,
): UseRentChangeBulkEditResult {
  const { propertyId, tenantId, rentChanges, moveInDate, moveOutDate } = options;
  const updateMutation = useUpdateRentChange();

  const [editMode, setEditMode] = useState(false);
  const [rows, setRows] = useState<Map<string, DraftRentChangeRow>>(new Map());
  const [needsDiscardConfirmation, setNeedsDiscardConfirmation] = useState(false);
  const [saveState, setSaveState] = useState<SaveState>({
    loading: false,
    rowErrors: new Map(),
    lastSaveAttemptCount: 0,
  });

  const originalById = useMemo(() => {
    const map = new Map<string, RentChange>();
    for (const rc of rentChanges) map.set(rc.id, rc);
    return map;
  }, [rentChanges]);

  const changesCount = useMemo(() => {
    let count = 0;
    for (const draft of rows.values()) {
      const original = originalById.get(draft.id);
      if (!original) continue;
      if (isRowChanged(draft, original)) count += 1;
    }
    return count;
  }, [rows, originalById]);

  const enterEditMode = useCallback(() => {
    setRows(initDraftRowsMap(rentChanges));
    setSaveState({ loading: false, rowErrors: new Map(), lastSaveAttemptCount: 0 });
    setNeedsDiscardConfirmation(false);
    setEditMode(true);
  }, [rentChanges]);

  const exitEditMode = useCallback(() => {
    setEditMode(false);
    setRows(new Map());
    setNeedsDiscardConfirmation(false);
    setSaveState({ loading: false, rowErrors: new Map(), lastSaveAttemptCount: 0 });
  }, []);

  const cancelEdit = useCallback(() => {
    if (changesCount > 0) {
      setNeedsDiscardConfirmation(true);
      return;
    }
    exitEditMode();
  }, [changesCount, exitEditMode]);

  const confirmDiscard = useCallback(() => {
    exitEditMode();
  }, [exitEditMode]);

  const abortDiscardConfirmation = useCallback(() => {
    setNeedsDiscardConfirmation(false);
  }, []);

  const setRow = useCallback((id: string, patch: SetRowPatch) => {
    setRows((prev) => {
      const next = new Map(prev);
      const existing = next.get(id);
      if (!existing) return prev;
      next.set(id, { ...existing, ...patch });
      return next;
    });
  }, []);

  const save = useCallback(async () => {
    const errors = new Map<string, ValidationResult | string>();
    const changedDrafts: DraftRentChangeRow[] = [];

    for (const draft of rows.values()) {
      const original = originalById.get(draft.id);
      if (!original || !isRowChanged(draft, original)) continue;
      const validation = validateDraftRow(draft, moveInDate, moveOutDate);
      if (validation !== "OK") {
        errors.set(draft.id, validation);
        continue;
      }
      changedDrafts.push(draft);
    }

    if (errors.size > 0) {
      setSaveState({ loading: false, rowErrors: errors, lastSaveAttemptCount: 0 });
      return;
    }

    const attemptCount = changedDrafts.length;
    setSaveState({ loading: true, rowErrors: new Map(), lastSaveAttemptCount: attemptCount });

    const results = await Promise.allSettled(
      changedDrafts.map((d) =>
        updateMutation.mutateAsync({
          propertyId,
          tenantId,
          rentChangeId: d.id,
          input: {
            effectiveFrom: d.effectiveFrom,
            coldRentCents: d.coldRentCents,
            sideCostsAdvanceCents: d.sideCostsAdvanceCents,
            notes: d.notes,
          },
        }),
      ),
    );

    const newErrors = new Map<string, ValidationResult | string>();
    const successIds = new Set<string>();
    results.forEach((res, idx) => {
      const draft = changedDrafts[idx];
      if (!draft) return;
      if (res.status === "rejected") {
        const msg = res.reason instanceof Error ? res.reason.message : String(res.reason);
        newErrors.set(draft.id, msg);
      } else {
        successIds.add(draft.id);
      }
    });

    if (newErrors.size === 0) {
      exitEditMode();
      return;
    }

    setRows((prev) => {
      const next = new Map(prev);
      for (const id of successIds) next.delete(id);
      return next;
    });
    setSaveState({ loading: false, rowErrors: newErrors, lastSaveAttemptCount: attemptCount });
  }, [
    rows,
    originalById,
    moveInDate,
    moveOutDate,
    propertyId,
    tenantId,
    updateMutation,
    exitEditMode,
  ]);

  useEffect(() => {
    if (!editMode || changesCount === 0) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [editMode, changesCount]);

  return {
    editMode,
    enterEditMode,
    cancelEdit,
    confirmDiscard,
    abortDiscardConfirmation,
    needsDiscardConfirmation,
    rows,
    setRow,
    changesCount,
    save,
    saveState,
  };
}
